import {
  BodyText,
  Callout,
  Collapsible,
  Skeleton,
  toast,
} from "@/components/ui";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components/EditableRecordButton";
import { isActionAvailable } from "@/utils/available-actions";
import { useEffect, useState } from "react";
import type { z } from "zod";
import { useCarePlanSummary } from "../network/useCarePlanSummary";
import { CareplanAvailableAction } from "../network/useCarePlanSummary";
import { useCreateRecommendation } from "../network/useCreateRecommendation";
import { useDeleteRecommendation } from "../network/useDeleteRecommendation";
import { useReferProviderCodes } from "../network/useProviderCodes";
import { useRecommendations } from "../network/useRecommendations";
import { useReferReasonCodes } from "../network/useReferReasonCodes";
import { useUpdateRecommendation } from "../network/useUpdateRecommendation";
import { useUpdateReferOut } from "../network/useUpdateReferOut";
import { useReferUrgencyCodes } from "../network/useUrgencyCodes";
import { ReferralEditor } from "./ReferralEditor";
import type { referralSchema } from "./schemas";

interface ReferralProps {
  carePlanId: string;
  encounterId: string;
}

export const Referral = ({ carePlanId, encounterId }: ReferralProps) => {
  const [open, setOpen] = useState(true);

  const {
    data: { recommendationTasks, amazonReferOutDetails } = {},
    isFetching,
    isError,
  } = useRecommendations({
    encounterId,
    carePlanId,
    enabled: open,
  });
  const referralDirections = recommendationTasks?.[0];

  const { data: reasonData } = useReferReasonCodes();
  const { data: urgencyData } = useReferUrgencyCodes();
  const { data: providerData } = useReferProviderCodes();

  const updateReferOut = useUpdateReferOut({ carePlanId, encounterId });
  const createRecommendation = useCreateRecommendation({
    encounterId,
    carePlanId,
  });
  const updateRecommendation = useUpdateRecommendation({
    encounterId,
    carePlanId,
    recommendationId: referralDirections?.id,
  });
  const deleteRecommendation = useDeleteRecommendation({
    encounterId,
    carePlanId,
    recommendationId: referralDirections?.id,
  });

  const reason = reasonData?.reasonCodes?.find(
    (item) => item.code === amazonReferOutDetails?.referralReasonCode,
  );
  const urgency = urgencyData?.urgencyCodes?.find(
    (item) => item.code === amazonReferOutDetails?.referralUrgencyCode,
  );
  const provider = providerData?.providerCodes?.find(
    (item) => item.code === amazonReferOutDetails?.referralProviderCode,
  );

  const { data: careplanSummary } = useCarePlanSummary(encounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const canWriteReferral = isActionAvailable(
    CareplanAvailableAction.ADD_REFERRAL,
    careplanAvailableActions,
  );
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (canWriteReferral && (!reason || !urgency || !provider)) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [reason, urgency, provider, canWriteReferral]);

  const onSubmit = async (values: z.infer<typeof referralSchema>) => {
    try {
      await updateReferOut.mutateAsync({
        referralProviderCode: values.provider,
        referralReasonCode: values.reason,
        referralUrgencyCode: values.urgency,
      });

      if (referralDirections?.description) {
        if (values.direction) {
          await updateRecommendation.mutateAsync({
            recommendationDescription: values.direction,
          });
        } else {
          await deleteRecommendation.mutateAsync();
        }
      } else if (values.direction) {
        await createRecommendation.mutateAsync({
          recommendationDescription: values.direction,
        });
      }

      setIsEditing(false);
    } catch (error) {
      toast.error("Failed to update referral. Please try again.");
    }
  };

  if (isError) {
    return (
      <Callout
        className="w-full"
        variant="danger"
        title="Something went wrong!"
        description="Failed to load refer out."
      />
    );
  }

  return (
    <Collapsible
      title="Refer Out"
      headerSize="sm"
      onOpenChange={setOpen}
      defaultOpen
    >
      <div className="flex flex-col">
        {isFetching && (
          <div className="grid gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
              <Skeleton className="h-6" key={i} />
            ))}
          </div>
        )}
        {isEditing && !isFetching && (
          <ReferralEditor
            onCancel={() => setIsEditing(false)}
            onSubmit={onSubmit}
            isLoading={updateReferOut.isPending}
            referOutDetails={amazonReferOutDetails}
            recommendationTask={recommendationTasks?.[0]}
          />
        )}
        {!isEditing && !isFetching && (
          <EditableRecordButton
            onClick={() => canWriteReferral && setIsEditing(true)}
            data-testid="editable-record-button"
            disabled={!canWriteReferral}
          >
            <div className="grid grid-cols-2 gap-2">
              <BodyText size="sm" bold>
                Referral Reason:
              </BodyText>
              <BodyText size="sm">{reason?.label || "N/A"}</BodyText>
              <BodyText size="sm" bold>
                Provider Type:
              </BodyText>
              <BodyText size="sm">{provider?.label || "N/A"}</BodyText>
              <BodyText size="sm" bold>
                Urgency:
              </BodyText>
              <BodyText size="sm">{urgency?.label || "N/A"}</BodyText>
              <BodyText size="sm" bold>
                Patient Directions:
              </BodyText>
              <BodyText
                size="sm"
                textColor={
                  referralDirections?.description ? "primary" : "tertiary"
                }
              >
                {referralDirections?.description || "None Specified"}
              </BodyText>
            </div>
          </EditableRecordButton>
        )}
      </div>
    </Collapsible>
  );
};
